// Operator login — posts real credentials to core-api (POST /api/auth/login via the session
// context). No demo credentials; invalid credentials surface the server's error.
import React from "react";
import { Button, FormField, Input, Banner } from "@/ds";
import { Icon } from "@/lib";
import { pathOf } from "../router";
import { useSession } from "../session";
import { Wordmark } from "../brand";
import { useT, LanguageSwitcher } from "../i18n";

/** Query flag a completed password reset (#1478) lands with, read once and then removed. */
export const PASSWORD_RESET_DONE_PARAM = "passwort-geaendert";

/** Shared with the password reset screens (#1478), which sit in the same frame. */
export function LoginBrandpane() {
  return (
    <div className="login__brandpane">
      <div className="login__mark">
        <Wordmark surface="deep" large />
      </div>
    </div>
  );
}

export function LoginScreen() {
  const { login } = useSession();
  const t = useT();
  const [email, setEmail] = React.useState("");
  const [pw, setPw] = React.useState("");
  const [show, setShow] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  // #1478: told once, then taken out of the address so a reload or a bookmark does not repeat it.
  const [resetDone] = React.useState(
    () => new URLSearchParams(window.location.search).has(PASSWORD_RESET_DONE_PARAM),
  );
  React.useEffect(() => {
    if (!resetDone) return;
    window.history.replaceState(window.history.state, "", pathOf({ name: "login" }));
  }, [resetDone]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      await login(email.trim(), pw);
    } catch (err: any) {
      setError(err?.status === 401 ? t("login.badCreds") : (err?.message || t("login.failed")));
      setBusy(false);
    }
  };

  return (
    <div className="login" data-testid="login-screen">
      <LoginBrandpane />
      <div className="preauth__lang"><LanguageSwitcher light /></div>
      <div className="login__formpane">
        <form className="login__form" onSubmit={submit}>
          <h1>{t("login.heading")}</h1>
          {resetDone && !error && (
            <div style={{ marginBottom: "var(--space-5)" }} data-testid="login-reset-done">
              <Banner tone="success" title={t("login.resetDoneTitle")}>{t("login.resetDoneBody")}</Banner>
            </div>
          )}
          {error && (
            <div style={{ marginBottom: "var(--space-5)" }}>
              <Banner tone="danger" title={t("login.failed")}>{error}</Banner>
            </div>
          )}
          <div className="stack-sm">
            <FormField label={t("login.email")} htmlFor="lg-mail">
              <Input id="lg-mail" type="email" value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                error={!!error} autoComplete="username" autoFocus required />
            </FormField>
            <FormField label={t("login.password")} htmlFor="lg-pw">
              <div className="login-password">
                <Input id="lg-pw" type={show ? "text" : "password"} value={pw} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPw(e.target.value)}
                  error={!!error} autoComplete="current-password" required />
                <button
                  type="button"
                  className="password-toggle iconbtn"
                  aria-label={show ? t("login.hidePw") : t("login.showPw")}
                  aria-pressed={show}
                  onClick={() => setShow((s) => !s)}
                >
                  <Icon name={show ? "eyeOff" : "eye"} size={18} />
                </button>
              </div>
            </FormField>
            <p className="login__assist">
              <a href={pathOf({ name: "passwordForgot" })}>{t("login.forgotLink")}</a>
            </p>
            <Button loading={busy} type="submit" fullWidth disabled={busy} aria-busy={busy}>
              {t("login.submit")}
            </Button>
          </div>
          <div className="login__foot">{t("login.foot")}</div>
        </form>
      </div>
    </div>
  );
}
